import { GridSortModel } from '@mui/x-data-grid';
import { AgentType } from '../interfaces/Agent';
import {
  Conversation,
  ConverstationStatus,
  DashboardData,
} from '../interfaces/Analytics';
import {
  DashboardSort,
  DashboardSortField,
} from '../interfaces/DashboardPaging';

/** The agent objects in a dashboard answer, skipping anything that is not one. */
const agentEntries = <E>(
  data: { message: Record<string, E>[] } | null | undefined
): E[] => {
  if (!data || !Array.isArray(data.message)) return [];
  return data.message
    .filter(entry => entry && typeof entry === 'object')
    .map(entry => entry[Object.keys(entry)[0]])
    .filter(Boolean);
};

/** Resolved calls in percent, from the backend totals. The rows are one page. */
export const getMeanSuccessRate = (data: DashboardData): number => {
  let resolved = 0;
  let total = 0;
  agentEntries(data).forEach(callEntry => {
    const calls = callEntry.total_calls_number || 0;
    total += calls;
    resolved += calls * (callEntry.total_success_rate || 0);
  });

  if (total === 0) return 0;
  return (resolved / total) * 100;
};

export const getTotalCalls = (data: DashboardData): number =>
  agentEntries(data).reduce(
    (total, entry) => total + (entry.total_calls_number || 0),
    0
  );

export const getTotalDuration = (data: DashboardData): number =>
  agentEntries(data).reduce(
    (total, entry) => total + (entry.total_calls_duration || 0),
    0
  );

export const getUniqueConversations = (data: DashboardData): Conversation[] => {
  const conversations: Conversation[] = [];
  const seen = new Set<string>();

  (data?.message ?? []).forEach(entry => {
    if (!entry || typeof entry !== 'object') return;
    const agent_id = Object.keys(entry)[0]; // Extract agent_id from key
    const callEntry = entry[agent_id];
    if (!Array.isArray(callEntry?.conversation_details)) return;

    callEntry.conversation_details.forEach(detail => {
      // same id twice would be two grid rows with one key
      if (detail.conversation_id) {
        if (seen.has(detail.conversation_id)) return;
        seen.add(detail.conversation_id);
      }
      conversations.push({
        agent_id, // Assign extracted agent_id
        agent_name: '--', // Placeholder, will be updated later
        call_cost: detail.call_cost,
        caller_phone_number: detail.caller_phone_number,
        conversation_id: detail.conversation_id,
        conversation_duration: detail.conversation_duration,
        conversation_summary: detail.conversation_summary,
        custom_phone_number: detail.custom_phone_number,
        data_collected: detail.data_collected,
        end_ts: detail.end_ts,
        start_ts: detail.start_ts,
        status: detail.status as ConverstationStatus,
        system_phone_number: detail.system_phone_number,
        messages: detail.messages,
        category: detail.category,
      });
    });
  });

  return conversations;
};

export const getTotalCallsPerMonth = (
  data: DashboardData
): Record<string, number> => {
  const totalCallsPerMonth: Record<string, number> = {};

  agentEntries(data).forEach(callEntry => {
    Object.entries(callEntry.monthly_calls_number ?? {}).forEach(
      ([month, calls]) => {
        totalCallsPerMonth[month] = (totalCallsPerMonth[month] || 0) + calls;
      }
    );
  });

  return totalCallsPerMonth;
};

type PerDayField = 'daily_calls_number' | 'daily_conversations_number';

/** Any dashboard answer, call or chat: one object per agent with its per-day counts. */
export type AnswerWithCountsPerDay = {
  message: Record<
    string,
    Partial<Record<PerDayField, Record<string, number>>>
  >[];
};

/** Per-day counts (YYYY-MM-DD) summed across agents. Undefined when the backend does not send them. */
export const getCountsPerDay = (
  data: AnswerWithCountsPerDay | null | undefined,
  field: PerDayField
): Record<string, number> | undefined => {
  const entries = agentEntries(data);
  if (entries.some(entry => !entry[field])) return undefined;

  const perDay: Record<string, number> = {};
  entries.forEach(entry => {
    Object.entries(entry[field] ?? {}).forEach(([day, count]) => {
      perDay[day] = (perDay[day] || 0) + count;
    });
  });
  return perDay;
};

export const formatToCurrency = (
  value: number,
  locales: string | string[] = 'lb-LU',
  currency = 'EUR',
  precision = 2,
  symbolPosition: 'before' | 'after' = 'before'
) => {
  const formatted = new Intl.NumberFormat(locales, {
    style: 'currency',
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(value);

  if (symbolPosition === 'after') {
    return formatted.replace(/(\D+)([\d\s.,]+)/, '$2 $1');
  }

  return formatted;
};

export const formatUnixDate = ({
  timestamp,
  locale = 'lb-LU',
  options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
}: {
  timestamp: number;
  locale?: string;
  options?: Intl.DateTimeFormatOptions;
}) => {
  return new Date(timestamp * 1000).toLocaleDateString(locale, options);
};

type RawDashboardData = {
  status: string;
  message: Record<string, any>[];
};

/**
 * Filter locally the dashboard data using an agent's ID.
 */
export const filteredDashboardData = (
  data: RawDashboardData,
  agentId: string
): RawDashboardData => {
  const filteredMessage = data?.message?.filter(entry => entry[agentId]);
  return { ...data, message: filteredMessage };
};

export const UNKNOWN_CATEGORY = 'Unknown';

/** Chip order: alphabetical, with Unknown last. */
export const sortCategoriesUnknownLast = (categories: string[]): string[] =>
  [...categories].sort((a, b) => {
    if (a === UNKNOWN_CATEGORY) return 1;
    if (b === UNKNOWN_CATEGORY) return -1;
    return a.localeCompare(b);
  });

/** Any dashboard answer, call or chat: one object per agent with its category counts. */
export type AnswerWithCategoryCounts = {
  message: Record<string, { category_counts?: Record<string, unknown> }>[];
};

/** What the table pages over: rows after chips and search. The total when the answer is unpaged. */
export const getTotalRows = (
  data:
    | { message: Record<string, { total_rows?: number }>[] }
    | null
    | undefined,
  fallback: number
): number => {
  const rows = agentEntries(data).map(entry => entry.total_rows);
  if (!rows.length || rows.some(count => typeof count !== 'number')) {
    return fallback;
  }
  return (rows as number[]).reduce((sum, count) => sum + count, 0);
};

/** Category counts from the backend, summed across agents. */
export const getCategoryCountsFromResponse = (
  data: AnswerWithCategoryCounts | null | undefined
): Record<string, number> => {
  const counts: Record<string, number> = {};
  agentEntries(data).forEach(agentEntry => {
    Object.entries(agentEntry?.category_counts ?? {}).forEach(
      ([category, count]) => {
        const name = category.trim();
        if (!name) return;
        counts[name] = (counts[name] || 0) + (Number(count) || 0);
      }
    );
  });
  return counts;
};

/** Chip options in display order: alphabetical, Unknown last. */
export const getCategoryOptionsFromCounts = (
  counts: Record<string, number>
): string[] => sortCategoriesUnknownLast(Object.keys(counts));

// Grid columns the backend can sort on, per agent type
const SORT_FIELDS: Record<
  AgentType,
  Partial<Record<string, DashboardSortField>>
> = {
  [AgentType.CALL]: {
    start_ts: 'start_ts',
    conversation_duration: 'duration',
    status: 'status',
  },
  [AgentType.CHAT]: {
    start_ts: 'start_ts',
    messages_count: 'messages_count',
    conversation_duration: 'duration',
  },
};

/** Whether the backend can order by this grid column. */
export const isDashboardSortable = (
  field: string,
  agentType: AgentType
): boolean => field in SORT_FIELDS[agentType];

/** The grid's sort as the backend wants it, or undefined for its default. */
export const toDashboardSort = (
  model: GridSortModel,
  agentType: AgentType
): DashboardSort | undefined => {
  const [first] = model;
  const field = first && SORT_FIELDS[agentType][first.field];
  if (!field || !first.sort) return undefined;
  return { field, direction: first.sort };
};

/** One string per sort, for query keys and change detection. */
export const dashboardSortKey = (sort: DashboardSort | undefined) =>
  sort ? `${sort.field}:${sort.direction}` : '';
